import { generatePath } from 'react-router-dom';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';

import { DeviceId } from './types';
import { cloverApi } from './index';

// Clover reader status (docs/CLOVER/reader-status-20261021/PLAN.md, API
// contract). Injected into the Clover API slice from this file so the
// payment endpoints in ./index.ts stay byte-for-byte. `word` and `tone` are
// written by the server; the screens only lay them out.
export type ReaderStatusKey =
  | 'ready'
  | 'login'
  | 'waiting'
  | 'in_sale'
  | 'offline'
  | 'unknown'
  | 'checking'
  // Never checked and its first check is still running (owner 22 Oct:
  // never "Can't check" for "never checked").
  | 'app_closed';

export type ReaderTone = 'ok' | 'bad' | 'warn' | 'muted';

export interface Reader {
  name: string;
  word: string;
  tone: ReaderTone;
  deviceId: number;
  eventId: number | null;
  status: ReaderStatusKey;
  checkedAt: string | null;
  eventName: string | null;
  lastSeenAt: string | null;
  statusSince: string | null;
}

export interface ReaderList {
  readers: Reader[];
}

export interface ReaderListRequest {
  eventId?: number;
}

export interface ReaderRequest extends DeviceId {
  fresh?: boolean;
}

// A 404 (another company's reader, or a Main without the route yet) must not
// send the app to the Not Found page (api/instance.ts).
const headers = { 'allow-not-found': 'true' };

// Read-only and deliberately tag-free: a sale's Clover mutations invalidate
// the 'Clover' tag, and must never trigger a status re-check mid-sale.
export const readerStatusApi = cloverApi.injectEndpoints({
  endpoints: (build) => ({
    readerStatusList: build.query<ReaderList, ReaderListRequest | void>({
      query: (params) => ({
        headers,
        method: 'get',
        url: addParamsToUrl(URL.CLOVER_READER_STATUS, params || {}),
      }),
    }),

    readerStatus: build.query<Reader, ReaderRequest>({
      query: ({ fresh, deviceId }) => ({
        headers,
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.CLOVER_READER_STATUS_DEVICE, {
            deviceId: String(deviceId),
          }),
          fresh ? { fresh: 1 } : undefined
        ),
      }),
    }),
  }),
});

export const {
  useReaderStatusQuery,
  useLazyReaderStatusQuery,
  useReaderStatusListQuery,
} = readerStatusApi;
